import type {FabricObjectPositionOrDimensionKeys} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import {degreesToRadians} from '@Utils/math.util';

/**
 * How far a control's own box reaches along the ITEM's axes once the item is rotated. A control is drawn
 * unrotated, so at 45° its corners stick out further along the item's right/down axes than half its width or
 * height would suggest — callers need this to inset a control far enough to stay inside a rotated item's edge.
 */
export const getControlHalfExtentOnItemAxes = (itemAngleDegrees: number, controlWidth: number, controlHeight: number): {onItemRight: number; onItemDown: number} => {
  const angleRadians = degreesToRadians(itemAngleDegrees);
  const cos = Math.abs(Math.cos(angleRadians));
  const sin = Math.abs(Math.sin(angleRadians));
  return {
    onItemRight: cos * (controlWidth / 2) + sin * (controlHeight / 2),
    onItemDown: sin * (controlWidth / 2) + cos * (controlHeight / 2),
  };
};

interface ControlAnchorParams {
  positionProperties: FabricObjectPositionOrDimensionKeys;
  controlWidth: number;
  controlHeight: number;
  /** Offset from the item's centre along the item's own right axis, before the item's rotation is applied. */
  offsetOnItemRight: number;
  /** Offset from the item's centre along the item's own down axis, before the item's rotation is applied. */
  offsetOnItemDown: number;
  /**
   * Angle to draw the control at. Pass the item's own angle to have it turn with the item and sit parallel to
   * the edge it is anchored to — its box then aligns with the item's axes, so half its width and height are
   * exactly how far it reaches, and {@link getControlHalfExtentOnItemAxes} is not needed. Defaults to upright.
   */
  controlAngleDegrees?: number;
}

/**
 * Screen-space top-left for a control pinned to a point expressed in the item's own rotated frame. Shared by the
 * custom item controls so they cannot drift apart on the centre/rotation maths; each one supplies only the
 * offset that defines where it sits (below the item, inside the top-right corner, …).
 */
export const getRotatedControlAnchor = ({
  positionProperties,
  controlWidth,
  controlHeight,
  offsetOnItemRight,
  offsetOnItemDown,
  controlAngleDegrees = 0,
}: ControlAnchorParams): CustomItemControlPositions => {
  const itemOnScreenWidth = positionProperties.scaledWidth * positionProperties.posterScale;
  const itemOnScreenHeight = positionProperties.scaledHeight * positionProperties.posterScale;
  const leftOnScreen = positionProperties.left * positionProperties.posterScale;
  const topOnScreen = positionProperties.top * positionProperties.posterScale;

  const angleRadians = degreesToRadians(positionProperties.angle);
  const cos = Math.cos(angleRadians);
  const sin = Math.sin(angleRadians);

  // Fabric reports left/top as the rotated item's top-left corner, so the centre is half the item's own
  // width/height away along its rotated axes.
  const centerX = leftOnScreen + (itemOnScreenWidth / 2) * cos - (itemOnScreenHeight / 2) * sin;
  const centerY = topOnScreen + (itemOnScreenWidth / 2) * sin + (itemOnScreenHeight / 2) * cos;

  const offsetX = cos * offsetOnItemRight - sin * offsetOnItemDown;
  const offsetY = sin * offsetOnItemRight + cos * offsetOnItemDown;

  // Subtracting half the control's box puts its centre on the anchor point, which is also what CSS rotates
  // about, so the anchor holds at any controlAngleDegrees.
  return {
    left: centerX + offsetX - Math.floor(controlWidth / 2),
    top: centerY + offsetY - Math.floor(controlHeight / 2),
    angle: controlAngleDegrees,
  };
};
